/**
 * Futures symbol-margin user API helpers
 * (GET/POST /v1/futures/symbol-settings, margin-type, leverage, batch, …)
 */

import { decNum } from "./futuresIncrementHelpers";

export function toApiMarginMode(uiMode) {
  const m = String(uiMode || "").toLowerCase();
  return m === "isolated" ? "ISOLATED" : "CROSS";
}

export function toUiMarginMode(apiMode) {
  return String(apiMode || "").toUpperCase() === "ISOLATED" ? "isolated" : "cross";
}

export function isIsolatedMarginMode(mode) {
  return String(mode ?? "").toUpperCase() === "ISOLATED";
}

export function formatMarginModeLabel(mode) {
  const m = String(mode ?? "").toUpperCase();
  if (m === "ISOLATED") return "Isolated";
  if (m === "CROSS") return "Cross";
  return mode || "—";
}

export function isCrossMarginMode(mode) {
  return String(mode ?? "").toUpperCase() === "CROSS";
}

/** Open-position Margin column — always `initial_margin` (both Isolated and Cross). */
export function bindPositionInitialMargin(pos) {
  return decNum(pos?.initial_margin);
}

/**
 * Open-position Margin Ratio — already a percent string (e.g. "5.7400").
 * Do not recompute Isolated math on Cross rows. Do not × 100.
 */
export function bindPositionMarginRatioPct(pos) {
  return decNum(pos?.margin_ratio);
}

export function formatMarginRatioPct(n) {
  if (!Number.isFinite(n)) return "—";
  return `${parseFloat(n.toFixed(4)).toString()}%`;
}

export function extractFuturesSymbolMarginError(res, fallback = "Request failed") {
  if (res == null) return fallback;
  if (typeof res === "string" && res.trim()) return res.trim();
  const nested = res?.error;
  if (typeof nested === "string" && nested.trim()) return nested.trim();
  if (nested && typeof nested === "object") {
    const msg = nested.message || nested.code;
    if (msg != null && String(msg).trim()) return String(msg).trim();
  }
  const top = res?.message || res?.msg;
  if (top != null && String(top).trim()) return String(top).trim();
  return fallback;
}

const CODE_COPY = {
  SYMBOL_REQUIRED: "Select a contract first.",
  INVALID_MARGIN_MODE: "Choose Cross or Isolated.",
  OPEN_POSITION: "Cannot change margin mode while you have an open position on this contract.",
  OPEN_ORDERS: "Cannot change margin mode while you have open orders on this contract.",
  INVALID_LEVERAGE: "Leverage is outside the allowed range for this contract.",
  INSUFFICIENT_FUTURES_BALANCE: "Insufficient futures balance for this leverage or margin change.",
  NOTHING_TO_ADJUST: "Enable leverage and/or margin mode before confirming.",
  SYMBOLS_REQUIRED: "No symbols to adjust.",
  BATCH_TOO_LARGE: "Too many symbols in one batch (max 50).",
  INVALID_AMOUNT: "Enter a valid margin amount greater than zero.",
  NO_OPEN_POSITION: "No open isolated position on this contract.",
  NOT_ISOLATED: "Add/remove margin is only available in Isolated mode.",
  ISOLATED_MARGIN_TOO_LOW: "Cannot remove that much isolated margin.",
  INTERNAL_ERROR: "Something went wrong. Please try again.",
};

export function formatFuturesSymbolMarginError(res, fallback = "Request failed") {
  const code = String(res?.error?.code || res?.code || "").toUpperCase();
  if (code && CODE_COPY[code]) return CODE_COPY[code];
  const raw = extractFuturesSymbolMarginError(res, "");
  if (!raw) return fallback;
  const upper = raw.toUpperCase();
  if (CODE_COPY[upper]) return CODE_COPY[upper];
  return raw;
}

/** Human summary for batch leverage-margin response. */
export function summarizeLeverageMarginBatch(data) {
  if (!data || typeof data !== "object") {
    return { title: "Batch adjust completed", detail: "" };
  }
  const succeeded = Number(data.succeeded) || 0;
  const failed = Number(data.failed) || 0;
  const total = Number(data.total) || succeeded + failed;
  const truncated = !!data.truncated;
  const results = Array.isArray(data.results) ? data.results : [];

  const failHints = [];
  for (const row of results) {
    if (!row || row.ok) continue;
    const parts = [];
    if (row.margin_mode_error) {
      parts.push(formatFuturesSymbolMarginError({ error: row.margin_mode_error }, "margin mode failed"));
    }
    if (row.leverage_error) {
      parts.push(formatFuturesSymbolMarginError({ error: row.leverage_error }, "leverage failed"));
    }
    failHints.push(`${row.symbol || "—"}: ${parts.join("; ") || "failed"}`);
    if (failHints.length >= 3) break;
  }

  let title;
  if (failed === 0) title = `Updated ${succeeded} market${succeeded === 1 ? "" : "s"}`;
  else if (succeeded === 0) title = `Could not update ${failed} market${failed === 1 ? "" : "s"}`;
  else title = `Updated ${succeeded} of ${total} markets`;

  const detailParts = [];
  if (truncated) detailParts.push("Only the first 50 listed contracts were included.");
  if (failHints.length) detailParts.push(failHints.join(" · "));

  return { title, detail: detailParts.join(" "), succeeded, failed, total, truncated };
}

export function applySymbolSettingsToUi(data, { setMarginMode, setLeverage } = {}) {
  if (!data) return;
  if (typeof setMarginMode === "function" && data.margin_mode != null) {
    setMarginMode(toUiMarginMode(data.margin_mode));
  }
  if (typeof setLeverage === "function" && data.leverage != null) {
    const lev = Math.round(Number(data.leverage));
    if (Number.isFinite(lev) && lev >= 1) setLeverage(lev);
  }
}

/**
 * Trail text for “Default leverage & margin mode” row.
 * Enabled → e.g. "10x / Cross"; off → "Off".
 */
export function formatBatchAdjustTrail(draft, fallbacks = {}) {
  const levOn = !!draft?.leverageEnabled;
  const modeOn = !!draft?.marginModeEnabled;
  if (!levOn && !modeOn) return "Off";

  const parts = [];
  if (levOn) {
    const lev = Math.round(Number(draft?.leverage ?? fallbacks.leverage));
    if (Number.isFinite(lev) && lev >= 1) parts.push(`${lev}x`);
  }
  if (modeOn) {
    const mode =
      draft?.marginMode === "isolated" || draft?.marginMode === "cross"
        ? draft.marginMode
        : fallbacks.marginMode === "isolated"
          ? "isolated"
          : "cross";
    parts.push(mode === "isolated" ? "Isolated" : "Cross");
  }
  return parts.length ? parts.join(" / ") : "Off";
}
